<template>
  <v-container class="fill-height d-flex justify-center align-center">
    <v-sheet>
      <v-btn color="primary" :to="{ name: 'AdminUserCreate' }" router> Create User </v-btn>
      <v-btn color="primary" @click="showAdd = !showAdd" router> Add Judge </v-btn>
      <v-divider class="my-4"></v-divider>

      <!-- Data Table -->
      <v-data-table-server
        v-model:items-per-page="pagination.limit"
        :items="judges"
        :items-length="count"
        :loading="isLoading"
        :headers="headers"
        :search="pagination.q"
        item-key="id"
        class="elevation-1"
        @update:options="handleLimitChange">
        <template v-slot:[`item.specialities`]="{ item }">
          <v-chip v-if="item.specialities.length > 0" v-for="spec of item.specialities">{{
            spec.name
          }}</v-chip>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn icon small @click="toggleSpecialities(item)">
            <v-icon icon="mdi-star-circle-outline"></v-icon>
          </v-btn>
          <v-btn icon small color="primary" @click="toggleDelete(item)">
            <v-icon icon="mdi-trash-can-outline"></v-icon>
          </v-btn>
        </template>
        <template v-slot:top>
          <v-text-field
            v-model="pagination.q"
            class="ma-2"
            density="compact"
            placeholder="Search users..."
            hide-details></v-text-field>
        </template>
      </v-data-table-server>
    </v-sheet>
  </v-container>
  <v-dialog v-model="showAdd">
    <UserFindComponent
      :role="judgeRole"
      @close="showAdd = false"
      @add="addUsers"
      :exclude="users"></UserFindComponent>
  </v-dialog>
  <v-dialog v-model="isSpecialitieseOpen">
    <JudgeSpecialtyAddEditComponent
      :judge="selectedJudge"
      @snack="throwSnack"
      @judge-specialty-cancled="isSpecialitieseOpen = false"
      @judge-specialty-saved="isSpecialitieseOpen = false"></JudgeSpecialtyAddEditComponent>
  </v-dialog>
  <v-dialog v-model="isDeleteOpen">
    <ConfirmDeleteComponent
      item="Judge"
      :is-open="isDeleteOpen"
      :description="`${selectedJudge?.judge.displayName}` || ''"
      @action-canceled="toggleDelete"
      @action-confirmed="removeJudge" />
  </v-dialog>
</template>
<script setup lang="ts">
import type { VDataTableServer } from 'vuetify/components';
import { IBranchYearRoundDTO, IPaginationDTO, PaginationDTO, RoleDTO } from '@/dtos';
import { useSnack } from '@/helpers';
import { SNACKSTATUS } from '@/stores/util';
import { computed, ref, watch } from 'vue';
import { useAdminJudgeStore } from '@/stores/modules/admin';
import UserFindComponent from '@/components/user/UserFindComponent.vue';
import { useUpdateStatusStore, useUserStore } from '@/stores';
import ConfirmDeleteComponent from '@/components/ui/ConfirmDeleteComponent.vue';
import { BranchYearJudgeDTO, IBranchYearJudgeDTO } from '@/dtos/branch.year.judge.dto';
import JudgeSpecialtyAddEditComponent from '@/components/judges/JudgeSpecialtyAddEditComponent.vue';

interface Props {
  round: IBranchYearRoundDTO;
}
const emit = defineEmits<{
  (e: 'snack', objType: string, snackType: SNACKSTATUS, err?: string): void;
}>();
const { throwSnack } = useSnack(emit);

const props = defineProps<Props>();
const round = computed(() => props.round);
const branchYear = computed(() => props.round.branchYear);
const branchId = computed(() => branchYear.value.branch?.id || '');
const yearId = computed(() => branchYear.value.year?.id || '');
const userStore = useUserStore();
const showAdd = ref<boolean>(false);
const roles = computed(() => userStore.getRolesQuery.data?.data);
const judgeRole = computed(
  () => roles.value?.filter((role) => role.role_name == 'Judge')[0] || new RoleDTO(),
);

const pagination = ref<IPaginationDTO>(new PaginationDTO());
const judgeStore = useAdminJudgeStore();
const statusStore = useUpdateStatusStore();
const { data, isLoading } = judgeStore.getBranchYearJudges(branchId, yearId, pagination);
const judges = computed(() => data.value?.data || []);
const users = computed(() => data.value?.data.map((judge) => judge.judge) || []);
const count = computed(() => data.value?.count ?? 0);
const isDeleteOpen = ref<boolean>(false);
const isSpecialitieseOpen = ref<boolean>(false);
const headers = [
  { title: 'First Name', key: 'judge.first_name' },
  { title: 'Last Name', key: 'judge.last_name' },
  { title: 'Email', key: 'judge.email_address' },
  { title: 'Specialities', key: 'specialities' },
  { title: 'Actions', key: 'actions', sortable: false },
];

const selectedJudge = ref<IBranchYearJudgeDTO | undefined>();
const handleLimitChange = (value: VDataTableServer) => {
  pagination.value.buildPagination(value);
};

const addUsers = async (usersIds: string[]) => {
  await judgeStore.add.mutateAsync({ roundId: round.value.id, userIds: usersIds });
};

const toggleDelete = (item: IBranchYearJudgeDTO) => {
  selectedJudge.value = new BranchYearJudgeDTO(item);
  isDeleteOpen.value = !isDeleteOpen.value;
};

const toggleSpecialities = (item: IBranchYearJudgeDTO) => {
  selectedJudge.value = new BranchYearJudgeDTO(item);
  isSpecialitieseOpen.value = !isSpecialitieseOpen.value;
};

const removeJudge = async () => {
  if (selectedJudge.value) {
    await judgeStore.remove.mutateAsync({
      roundId: round.value.id,
      judgeId: selectedJudge.value.judge.id,
    });
  }
};

watch(
  () => statusStore.getStatus(`branch-year-judge-${selectedJudge.value?.id}`),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    const key = `branch-year-judge-${selectedJudge.value?.id}`;
    emit('snack', 'Judge', value, statusStore.getError(key));
    selectedJudge.value = undefined;
    isDeleteOpen.value = false;
  },
);

watch(
  () => statusStore.getStatus(`branch-year-judges-${branchYear.value?.id}`),
  (value: SNACKSTATUS | undefined) => {
    if (!value) {
      return;
    }
    const key = `branch-year-judges-${branchYear.value?.id}`;
    emit('snack', 'Judge', value, statusStore.getError(key));
    showAdd.value = false;
  },
);
</script>
